import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Practitioner } from '@medplum/fhirtypes';
import { usePermissions } from '@/contexts/PermissionContext';
import { resolvePermissionsFromProfile } from '@/features/rbac';
import type { Role } from '@/pages/users/types/rbac';
import { canEditSupervisionRelationships } from '@/lib/supervision/canEditSupervisionRelationships';
import {
  filterSuperviseeCandidates,
  filterSupervisorCandidates,
  getViewedSupervisorId,
  toSupervisionOption,
  toSupervisionTableRow,
  type PractitionerSupervisionOption,
  type SupervisionTableRow,
} from '@/lib/supervision/supervisionDisplay';
import { getSupervisionStartedAt, resolveSuperviseeIds } from '@/lib/supervision/supervisionRelationships';
import { updateSupervisorSupervisees } from '@/lib/supervision/supervisionService';
import {
  practitionerRequiresCosignature,
  setRequiresCosignature,
} from '@unify/fhir-model/resources/practitioner';

export interface SupervisionCardData {
  loading: boolean;
  error: string | null;
  practitioner: Practitioner | null;
  viewedRoles: Role[];
  showProviderSection: boolean;
  showSupervisorSection: boolean;
  canEdit: boolean;
  supervisorId: string | undefined;
  superviseeIds: string[];
  requiresCosignature: boolean;
  supervisorOption: PractitionerSupervisionOption | null;
  supervisorRow: SupervisionTableRow | null;
  superviseeOptions: PractitionerSupervisionOption[];
  superviseeRows: SupervisionTableRow[];
  supervisorCandidates: PractitionerSupervisionOption[];
  superviseeCandidates: PractitionerSupervisionOption[];
  refresh: () => void;
  /** Optimistic/local patch — avoids stale readResource cache after cosignature toggle. */
  setRequiresCosignatureLocal: (value: boolean) => void;
}

function practiceNameMapFromUsers(
  allUsers: { id: string; practiceId?: string; practiceName?: string }[],
  practices: { id: string; name: string }[],
): Map<string, string> {
  const byOrg = new Map<string, string>();
  for (const pr of practices) {
    byOrg.set(pr.id, pr.name);
  }
  for (const u of allUsers) {
    if (u.practiceId && u.practiceName) {
      byOrg.set(u.practiceId, u.practiceName);
    }
  }
  return byOrg;
}

export function useSupervisionCardData(
  practitionerId: string,
  practiceId: string | undefined,
  practices: { id: string; name: string }[],
  allUsers: { id: string; practiceId?: string; practiceName?: string }[],
  refreshToken = 0,
): SupervisionCardData {
  const medplum = useMedplum();
  const { availableRoles } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [allPractitioners, setAllPractitioners] = useState<Practitioner[]>([]);
  const [reloadToken, setReloadToken] = useState(0);
  const syncRepairAttemptedRef = useRef<string | null>(null);

  const refresh = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  const setRequiresCosignatureLocal = useCallback((value: boolean) => {
    setPractitioner((prev) => (prev ? setRequiresCosignature(prev, value) : prev));
  }, []);

  const unifyScope = availableRoles.some((r) => r === 'unify-admin');
  const canEdit = canEditSupervisionRelationships(availableRoles);
  const practiceNameByOrgId = useMemo(
    () => practiceNameMapFromUsers(allUsers, practices),
    [allUsers, practices],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        medplum.invalidateSearches('Practitioner');
        const [viewed, all] = await Promise.all([
          medplum.readResource('Practitioner', practitionerId, { cache: 'no-cache' }),
          medplum.searchResources('Practitioner', { _count: '500' }),
        ]);
        if (cancelled) return;
        setPractitioner(viewed);
        setAllPractitioners(all);
      } catch {
        if (!cancelled) {
          setError('Could not load supervision data.');
          setPractitioner(null);
          setAllPractitioners([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, practitionerId, reloadToken, refreshToken]);

  const viewedRoles = useMemo(
    () => (practitioner ? resolvePermissionsFromProfile(practitioner).availableRoles : []),
    [practitioner],
  );

  const showSupervisorSection = viewedRoles.includes('supervisor');
  // A supervisor is never supervised (PRD_ROLE_TRANSITIONS), so the "who supervises
  // this person" section disappears as soon as they hold supervisor.
  const showProviderSection = viewedRoles.includes('provider') && !showSupervisorSection;

  const supervisorId = practitioner ? getViewedSupervisorId(practitioner) : undefined;
  const { superviseeIds, needsReverseSync } = useMemo(() => {
    if (!practitioner || !showSupervisorSection) {
      return { superviseeIds: [], needsReverseSync: false };
    }
    return resolveSuperviseeIds(practitioner, allPractitioners);
  }, [practitioner, showSupervisorSection, allPractitioners]);

  useEffect(() => {
    syncRepairAttemptedRef.current = null;
  }, [practitionerId, practitioner?.meta?.versionId]);

  useEffect(() => {
    if (!canEdit || !showSupervisorSection || !needsReverseSync || loading || superviseeIds.length === 0) {
      return;
    }

    const repairKey = `${practitionerId}:${superviseeIds.join(',')}`;
    if (syncRepairAttemptedRef.current === repairKey) {
      return;
    }
    syncRepairAttemptedRef.current = repairKey;

    let cancelled = false;
    (async () => {
      try {
        await updateSupervisorSupervisees(medplum, practitionerId, superviseeIds);
        if (!cancelled) {
          refresh();
        }
      } catch {
        // Silent — admin can repair manually via add/remove if sync fails.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    canEdit,
    showSupervisorSection,
    needsReverseSync,
    loading,
    superviseeIds,
    practitionerId,
    medplum,
    refresh,
  ]);

  const practitionersById = useMemo(
    () => new Map(allPractitioners.filter((p) => p.id).map((p) => [p.id!, p])),
    [allPractitioners],
  );

  const supervisorOption = useMemo(() => {
    if (!supervisorId) return null;
    const sup = practitionersById.get(supervisorId);
    return sup ? toSupervisionOption(sup, practiceNameByOrgId) : null;
  }, [supervisorId, practitionersById, practiceNameByOrgId]);

  const supervisorRow = useMemo((): SupervisionTableRow | null => {
    if (!supervisorId || !practitioner) return null;
    const sup = practitionersById.get(supervisorId);
    if (!sup) return null;
    const row = toSupervisionTableRow(sup, practiceNameByOrgId);
    return { ...row, startedAt: getSupervisionStartedAt(practitioner) ?? row.startedAt };
  }, [supervisorId, practitioner, practitionersById, practiceNameByOrgId]);

  const superviseeRows = useMemo(
    () =>
      superviseeIds
        .map((id) => practitionersById.get(id))
        .filter((p): p is Practitioner => !!p)
        .map((p) => toSupervisionTableRow(p, practiceNameByOrgId)),
    [superviseeIds, practitionersById, practiceNameByOrgId],
  );

  const supervisorCandidates = useMemo(
    () =>
      filterSupervisorCandidates(allPractitioners, {
        practiceOrgId: practiceId,
        unifyScope,
        excludeId: practitionerId,
      }).map((p) => toSupervisionOption(p, practiceNameByOrgId)),
    [allPractitioners, practiceId, unifyScope, practitionerId, practiceNameByOrgId],
  );

  const superviseeCandidates = useMemo(
    () =>
      filterSuperviseeCandidates(allPractitioners, {
        practiceOrgId: practiceId,
        unifyScope,
        excludeId: practitionerId,
      }).map((p) => toSupervisionOption(p, practiceNameByOrgId)),
    [allPractitioners, practiceId, unifyScope, practitionerId, practiceNameByOrgId],
  );

  return {
    loading,
    error,
    practitioner,
    viewedRoles,
    showProviderSection,
    showSupervisorSection,
    canEdit,
    supervisorId,
    superviseeIds,
    requiresCosignature: practitionerRequiresCosignature(practitioner ?? undefined),
    supervisorOption,
    supervisorRow,
    superviseeOptions: superviseeRows.map((row) => ({
      value: row.id,
      name: row.name,
      label: [row.name, row.credentials, row.practiceName].filter(Boolean).join(' · '),
      credentials: row.credentials,
      practiceName: row.practiceName,
    })),
    superviseeRows,
    supervisorCandidates,
    superviseeCandidates,
    refresh,
    setRequiresCosignatureLocal,
  };
}
